import { createBrowserRouter } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { RootLayout } from '@/layouts/RootLayout'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { LocalizationProvider } from '@/contexts/LocalizationContext'
import { DashboardPage } from '@/pages/DashboardPage'
import { OrdersPage } from '@/pages/OrdersPage'
import { OrderDetailsPage } from '@/pages/orders/OrderDetailsPage'
import { CreateOrderPage } from '@/pages/CreateOrderPage'
import { ProvidersPage } from '@/pages/ProvidersPage'
import { MyServicesPage } from '@/pages/MyServicesPage'
import { FinancialsPage } from '@/pages/FinancialsPage'
import { AccountSettingsPage } from '@/pages/AccountSettingsPage'
import { UsersPage } from '@/pages/UsersPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { ProviderRegistrationPage } from '@/pages/ProviderRegistrationPage'
import { VerifyOTPPage } from '@/pages/VerifyOTPPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { AccessDeniedPage } from '@/pages/AccessDeniedPage'
import { HomeRoute } from '@/components/auth/HomeRoute'

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // Public routes
      {
        path: '/login',
        element: <LoginPage />,
      },
      {
        path: '/register',
        element: <RegisterPage />,
      },
      {
        path: '/provider-registration',
        element: <ProviderRegistrationPage />,
      },
      {
        path: '/verify-otp',
        element: <VerifyOTPPage />,
      },
      // Protected routes (LocalizationProvider ensures route content has access to locale/t)
      {
        path: '/',
        element: (
          <ProtectedRoute>
            <LocalizationProvider>
              <AppLayout />
            </LocalizationProvider>
          </ProtectedRoute>
        ),
        children: [
          {
            index: true,
            element: <HomeRoute />,
          },
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="Dashboard">
                <DashboardPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'orders',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="Order">
                <OrdersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'orders/:orderId',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="Order">
                <OrderDetailsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'create-order',
            element: (
              <ProtectedRoute
                businessClientOrderCreationOnly
                caslAction="manage"
                caslSubject="Order"
              >
                <CreateOrderPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'create-order/*',
            element: (
              <ProtectedRoute
                businessClientOrderCreationOnly
                caslAction="manage"
                caslSubject="Order"
              >
                <CreateOrderPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'providers',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="Provider">
                <ProvidersPage />
              </ProtectedRoute>
            ),
          },
          {
            // Deliberately not CASL-gated: there is no provider-self subject, and
            // AbilityProvider prefers the ENTITY/ORGANIZATION profile's rules anyway.
            // The page gates itself on holding a PROVIDER profile.
            path: 'my-services',
            element: (
              <ProtectedRoute>
                <MyServicesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'financials',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="Finance">
                <FinancialsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'account-settings',
            element: <AccountSettingsPage />,
          },
          {
            path: 'users',
            element: (
              <ProtectedRoute caslAction="read" caslSubject="System">
                <UsersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'notifications',
            element: <NotificationsPage />,
          },
          {
            path: 'access-denied',
            element: <AccessDeniedPage />,
          },
        ],
      },
      // 404
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
